/**
 * useAllOperations
 *
 * Client-side prefetch + search infrastructure for the operation picker.
 * Mirrors the upstream `libs/designer-v2/src/lib/core/queries/browse.ts`
 * pattern (three sources: Azure paginated, Custom paginated, BuiltIn single
 * fetch) and layers an IndexedDB cache over managed-API operations so subsequent
 * sessions hydrate those before network calls land. Runtime built-ins are
 * fetched from the selected app.
 *
 *   ┌─ usePrefetchOperations ─┐
 *   │  ├ Azure (paged)        │  Cached + merged
 *   │  ├ Custom (paged)       │ ──────────────►  useAllOperations
 *   │  └ BuiltIn (single)     │
 *   └─────────────────────────┘
 *
 * See {@link usePrefetchOperations} for the recommended root-component wiring
 * and {@link useAllOperations} (plus {@link useAllTriggers} /
 * {@link useAllActions}) for the consumer hooks.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import {
  readRegionGates,
  seedRegionGatesIfMissing,
  REGION_GATES_CHANGED_EVENT,
} from '@/services/regionGatesCache';
import {
  fetchAllAzureConnectors,
  fetchAzureOperationsByPage,
  fetchBuiltInOperations,
  type Connector,
  type DiscoveryOperation,
  type PagedOperationsResult,
} from '@/services/operationsService';
import { getClientBuiltInOperations } from '@/data/clientOperations';
import {
  readOperationsCache,
  trimOperationForCache,
  writeOperationsCache,
} from '@/services/operationsCache';
import { isLocalMode } from '@/services/localRuntimeClient';
import { isArmMockMode } from '@/services/armConfig';
import {
  buildConnectorIdentityIndex,
  isMalformedManagedApiId,
  isMalformedOpId,
  repairAzureOpIdentity,
} from '@/services/operationsRepair';
import { countMalformedAzureIds } from '@/services/operationsDebug';
import { getOperationKey } from '@/utils/operationKey';

/** Stable query-cache settings shared across the three source queries. */
const queryOpts = {
  staleTime: 1000 * 60 * 60 * 24, // 24 hours — matches the IDB TTL.
  gcTime: 1000 * 60 * 60 * 24,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

/**
 * Built-in operations are app-specific and change with runtime / bundle
 * rollouts, so they use a much shorter cache window than the regional
 * managed-API catalog (which is stable and IDB-backed for 24h). Shared with
 * the browse-mode copy in `useOperations.ts` since both queries key alike.
 */
export const BUILTIN_OPERATIONS_CACHE_MS = 1000 * 60 * 60 * 2; // 2 hours

/** Exposed for unit tests — internal helpers, not part of the public hook API. */
export const __testables = {
  isMalformedManagedApiId,
  isMalformedOpId,
  buildConnectorIdentityIndex,
  repairAzureOpIdentity,
};

/**
 * Minimum age of a cached entry before {@link useBackgroundOperationsRefresh}
 * will trigger a network refetch on cache hit. Below this threshold the
 * cache is treated as "recent enough" — back-to-back reloads (Ctrl+R, Ctrl+R)
 * shouldn't cost another full pagination of /apiOperations.
 *
 * Kept short (5 min) because the cache can be poisoned by a silently
 * truncated `/apiOperations` pagination on the very first write for a
 * region: nothing errored, `hasNextPage` went false early, the write
 * gates passed, and the partial corpus established the HWM floor. The only
 * thing that can heal that case is a fresh end-to-end pagination that
 * returns more ops than the cache. With the per-tab `backgroundRefreshedScopes`
 * Set this still amortizes to one extra pagination per fresh tab per region.
 */
const BACKGROUND_REFRESH_MIN_CACHE_AGE_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Bounded retry budget + base backoff for a self-heal attempt that fails
 * transiently (token not ready yet, a network blip mid-pagination). The sole
 * production caller (`usePrefetchOperations` in `AppLayout`) is persistently
 * mounted and never remounts, so retries are driven by reactive state
 * (`refreshTick`) rather than a remount — unmarking the module-level ledger
 * alone would never re-run the effect.
 */
const BACKGROUND_REFRESH_MAX_RETRIES = 3;
export const BACKGROUND_REFRESH_RETRY_BASE_MS = 2000; // 2s, then 4s, then 8s.

/**
 * Per-scope record of background refreshes already attempted in this tab.
 * Keyed by `{project}::{region}`. Resets on full page reload, which is the
 * intended granularity — we only want to pay for the extra pagination once
 * per session per region, no matter how many times the picker is opened.
 *
 * Module-level so the entry survives the React lifecycle of any individual
 * `usePrefetchOperations` consumer (the picker can mount/unmount many times
 * while the user navigates).
 */
const backgroundRefreshedScopes = new Set<string>();

/** Test helper: clear the per-session refresh ledger. */
export const __resetBackgroundRefreshedScopesForTests = (): void => {
  backgroundRefreshedScopes.clear();
};

/**
 * Resolve the scope used by the operation-catalog queries.
 *   - `project`    — raw value from the store, sent to the backend as-is
 *                    (project names are treated as opaque identifiers).
 *   - `projectKey` — lowercased, for query/cache/scope keys so picker views
 *                    differing only in casing share a cache slot.
 *   - `region`     — lowercased; only used as a cache discriminator.
 */
const useScopeKey = () => {
  const selectedProject = useAppStore((s) => s.selectedProject);
  const selectedApp = useAppStore((s) => s.selectedApp);
  const selectedLocation = useAppStore((s) => s.selectedLocation);

  return useMemo(() => {
    const project = selectedProject ?? '';
    const projectKey = project.toLowerCase();
    const region = (selectedApp?.location ?? selectedLocation ?? '').toLowerCase();
    return { project, projectKey, region, hasScope: !!project && !!region };
  }, [selectedProject, selectedApp, selectedLocation]);
};

/**
 * Read-through to IndexedDB for the current scope. The result is loaded once
 * per scope-change and exposed so the lazy queries can skip network fetches
 * when a fresh cached corpus is available.
 */
const useCachedOperations = () => {
  const { region, hasScope } = useScopeKey();
  return useQuery({
    queryKey: ['operationsCache', region],
    queryFn: () => readOperationsCache(region),
    enabled: hasScope,
    ...queryOpts,
  });
};

/**
 * Connector identity index from `/managedApis`. Shares its query key with
 * `useAzureConnectorGroups` in `useOperations.ts` so React Query deduplicates
 * the network call across the picker's browse-mode hook and this one — no
 * extra round-trip.
 *
 * Returns the prebuilt Map so consumers don't re-derive it on every render.
 * Falls back to an empty Map while the fetch is in flight, which makes the
 * repair a no-op until the index hydrates (the merge useMemo below picks the
 * repair back up once the index resolves).
 */
const useAzureConnectorIdentityIndex = (): Map<string, { id: string; name: string }> => {
  const { getOttoToken } = useAuth();
  const { project, projectKey, hasScope } = useScopeKey();

  const { data: connectors } = useQuery({
    // Must match `useAzureConnectorGroups` exactly so React Query reuses the
    // cached result instead of issuing a second `/managedApis` pagination.
    queryKey: ['connectorGroups', 'azure', projectKey],
    enabled: hasScope && (!isLocalMode() || isArmMockMode()),
    queryFn: async () => {
      if (!project) return [];
      if (isLocalMode() && !isArmMockMode()) return [];
      const token = await getOttoToken();
      if (!token && !isArmMockMode()) return [];
      return fetchAllAzureConnectors({
        projectName: project,
        accessToken: token ?? '',
      });
    },
    ...queryOpts,
  });

  return useMemo(() => buildConnectorIdentityIndex(connectors), [connectors]);
};

/**
 * Lazy paginated query for Azure managed-API operations. The query stays in
 * loading state until the cache lookup resolves; when the cache holds a fresh
 * entry, we feed its Azure-typed operations as the first synthetic page so the
 * preloader sees `hasNextPage === false` and no network request fires.
 */
const useAzureOperationsLazyQuery = () => {
  const { getOttoToken } = useAuth();
  const { project, projectKey, region, hasScope } = useScopeKey();
  const { data: cached, isLoading: cacheLoading } = useCachedOperations();

  // Filter the cached corpus down to Azure-sourced operations (heuristic:
  // operations whose `type` indicates a managedApi operation). Treat an
  // empty result as a cache miss — a real Otto project always has thousands
  // of managed-API operations, so 0 means either the cache was written before
  // the network fetch completed (race) or the `type` discriminator drifted.
  // Either way, falling back to the network is correct.
  const cachedAzure = useMemo<DiscoveryOperation[] | null>(() => {
    if (!cached) return null;
    const azure = cached.operations.filter((op) => isAzureOperation(op));
    return azure.length > 0 ? azure : null;
  }, [cached]);

  return useInfiniteQuery({
    queryKey: ['operationsLazy', 'azure', projectKey, region],
    initialPageParam: undefined as string | undefined,
    enabled: hasScope && !cacheLoading,
    queryFn: async ({ pageParam }) => {
      if (cachedAzure) {
        // First (and only) synthetic page from cache. Returning no nextLink
        // ends pagination immediately.
        return { data: cachedAzure } satisfies PagedOperationsResult;
      }
      const token = await getOttoToken();
      // Block when we're cloud-bound without a token; local/arm-mock paths
      // tolerate an empty token via fetchWithAuth's mock branch.
      if (!token && !isLocalMode() && !isArmMockMode()) {
        return { data: [] } satisfies PagedOperationsResult;
      }
      return fetchAzureOperationsByPage(
        { projectName: project, accessToken: token ?? '' },
        pageParam,
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextLink,
    ...queryOpts,
  });
};

/**
 * Custom-connector operations. Otto's control plane does not yet expose a
 * paginated custom-API operations endpoint, so this hook resolves to an empty
 * result and is wired purely for parity with the upstream three-source shape.
 * When the endpoint lands, swap this body for the real paginated fetch.
 */
const useCustomOperationsLazyQuery = () => {
  const { projectKey, region, hasScope } = useScopeKey();
  return useInfiniteQuery({
    queryKey: ['operationsLazy', 'custom', projectKey, region],
    initialPageParam: undefined as string | undefined,
    enabled: hasScope,
    queryFn: async (): Promise<PagedOperationsResult> => ({ data: [] }),
    getNextPageParam: () => undefined,
    ...queryOpts,
  });
};

/**
 * Runtime built-ins are app-specific, so never hydrate them from the regional
 * IndexedDB cache. React Query still deduplicates requests for the selected app.
 */
export const useBuiltInOperationsQuery = () => {
  const { getOttoToken } = useAuth();
  const { project, projectKey, region, hasScope } = useScopeKey();
  const selectedApp = useAppStore((s) => s.selectedApp);

  const enabled = hasScope && !!selectedApp?.name;

  return useQuery({
    queryKey: ['operations', 'builtin', projectKey, selectedApp?.name?.toLowerCase() ?? ''],
    enabled,
    queryFn: async (): Promise<DiscoveryOperation[]> => {
      if (!selectedApp?.name) return [];
      const token = await getOttoToken();
      // Read gates fresh from IndexedDB each fetch so a devtools change picked
      // up via query invalidation applies without a stale render-captured value.
      const regionGates = await readRegionGates();
      // Empty-vs-error is decided inside fetchBuiltInOperations against the
      // pre-gate runtime response (an incomplete fetch rejects; a region-gated
      // empty result is a valid success). The cache-write gate in
      // usePrefetchOperations still relies on builtinStatus === 'success'.
      return fetchBuiltInOperations({
        projectName: project,
        appName: selectedApp.name,
        accessToken: token ?? '',
        location: region,
        regionGates,
      });
    },
    ...queryOpts,
    // Built-ins track the app's runtime, so poll on the shorter window instead
    // of the 24h regional catalog. `staleTime` alone won't refetch: this query
    // stays active (mounted via usePrefetchOperations in AppLayout) and
    // refetch-on-mount/focus/reconnect are disabled, so an explicit
    // `refetchInterval` is what actually recovers a stale/partial result. The
    // per-{project,app} key means each app heals on its own timer, independent
    // of the regional Azure self-heal.
    staleTime: BUILTIN_OPERATIONS_CACHE_MS,
    gcTime: BUILTIN_OPERATIONS_CACHE_MS,
    refetchInterval: BUILTIN_OPERATIONS_CACHE_MS,
    // `refetchInterval` pauses in a hidden tab, so a tick missed while
    // backgrounded would leave the catalog stale until the next tick. Refetch
    // on focus too — bounded by the 2h staleTime, so it only fires when stale.
    refetchOnWindowFocus: true,
  });
};

/**
 * Merged corpus across all three sources plus the hardcoded client-side
 * built-ins (HTTP, Control, Variables, …). De-duplicates on connector + operation ID;
 * later sources override earlier ones so richer runtime metadata wins over
 * client fallbacks.
 *
 * `isLoading` is true while any source is still fetching its first page or has
 * more pages to fetch — callers should treat the data as "best-so-far" and
 * re-render as new pages arrive.
 */
export const useAllOperations = () => {
  const {
    data: azureOperations,
    isLoading: azureLoading,
    hasNextPage: azureHasNextPage,
  } = useAzureOperationsLazyQuery();
  const {
    data: customOperations,
    isLoading: customLoading,
    hasNextPage: customHasNextPage,
  } = useCustomOperationsLazyQuery();
  const { data: builtinOperations, isLoading: builtinLoading } = useBuiltInOperationsQuery();
  // Reliable identity source for normalizing malformed Azure-op `api.id`
  // / `api.name` fields. Empty Map until `/managedApis` resolves —
  // normalization is a no-op during that window, then re-runs via the
  // merge useMemo's dependency.
  const connectorIdentityIndex = useAzureConnectorIdentityIndex();

  const data = useMemo(() => {
    const merged = new Map<string, DiscoveryOperation>();
    const addAll = (
      ops: DiscoveryOperation[] | undefined,
      { repair = false }: { repair?: boolean } = {},
    ) => {
      if (!ops) return;
      for (const op of ops) {
        if (!op?.id) continue;
        // Repair runs even when the connector identity index is empty —
        // the {@link CANONICAL_DISPLAY_NAMES} fallback inside
        // `repairAzureOpIdentity` handles popular Microsoft / Google
        // brands that the tenant's `/managedApis` list may not include
        // (e.g. only publisher forks of Outlook.com exist, no canonical).
        // Dedupe on the REPAIRED id, not the original — malformed op.ids
        // of shape `/automationProjects/{p}/managedApis//{opName}` collide
        // across canonical brands and silently drop ops from the corpus
        // (see `isMalformedOpId`).
        if (repair) {
          const fixed = repairAzureOpIdentity(op, connectorIdentityIndex);
          merged.set(getOperationKey(fixed.id, fixed.properties?.api?.id), fixed);
        } else {
          merged.set(getOperationKey(op.id, op.properties?.api?.id), op);
        }
      }
    };

    // Client-side fallback baseline — provides Control/Variable/HTTP shells
    // immediately so the first paint shows something useful before any
    // network roundtrip resolves.
    addAll(getClientBuiltInOperations());

    // Normalize Azure ops only — the malformed-id bug is specific to the
    // `/apiOperations` endpoint. Custom + built-ins return well-formed ids.
    // Normalization rewrites stripped `api.id`
    // (e.g. `/automationProjects/{p}/managedApis/`) to canonical short
    // form (e.g. `/managedApis/sharepointonline`) by joining the op's
    // `api.displayName` against the `/managedApis` connector identity
    // index — the only field that survives the server's malformed-payload
    // shape. This single normalization pass replaces the previous
    // backfill architecture (which fired redundant per-connector fetches
    // to recover ops the bulk endpoint actually returned, just in
    // malformed shape).
    addAll(azureOperations?.pages.flatMap((page) => page.data), { repair: true });
    addAll(customOperations?.pages.flatMap((page) => page.data));
    // Runtime built-ins ship their own Compose entry under the "Data
    // Operations New" connector label, which collides with Otto's curated
    // client-side Compose (id 'compose'). The two entries have different
    // ids so the Map dedupe above can't collapse them — strip the runtime
    // copy here so the search corpus only exposes the curated manifest.
    // Mirrors the same guard in useSearchOperations (useOperations.ts).
    addAll(
      builtinOperations?.filter(
        (op) => op.properties?.operationType?.toLowerCase() !== 'compose',
      ),
    );

    return Array.from(merged.values());
  }, [
    azureOperations,
    customOperations,
    builtinOperations,
    connectorIdentityIndex,
  ]);

  const isLoading = useMemo(
    () =>
      azureLoading ||
      customLoading ||
      builtinLoading ||
      azureHasNextPage ||
      customHasNextPage ||
      false,
    [
      azureLoading,
      customLoading,
      builtinLoading,
      azureHasNextPage,
      customHasNextPage,
    ],
  );

  return useMemo(() => ({ data, isLoading }), [data, isLoading]);
};

/** Triggers-only view of {@link useAllOperations}. */
export const useAllTriggers = () => {
  const all = useAllOperations();
  return useMemo(
    () => ({
      ...all,
      data: all.data.filter((op) => !!op.properties?.trigger),
    }),
    [all],
  );
};

/** Actions-only view of {@link useAllOperations}. */
export const useAllActions = () => {
  const all = useAllOperations();
  return useMemo(
    () => ({
      ...all,
      data: all.data.filter((op) => !op.properties?.trigger),
    }),
    [all],
  );
};

/**
 * Self-healing background refresh for the IDB cache.
 *
 * When the picker hydrates from a cache entry that's older than
 * {@link BACKGROUND_REFRESH_MIN_CACHE_AGE_MS}, this hook silently re-fetches
 * the Azure operations corpus end-to-end (all pages) and compares the result
 * against the cached payload. If the network corpus is materially larger, it
 * overwrites the cache entry and invalidates the in-memory queries so the
 * picker picks up the recovered data without a page reload.
 *
 * The purpose is recovery: a previously-stuck partial cache (e.g. a one-off
 * transient that wrote a truncated corpus, or a v3-initial entry without a
 * high-water-mark) gets healed automatically the next time the picker mounts.
 * Without this, a partial cache persists for the full 24h TTL and the user
 * can't search for connectors that aren't in the cached subset.
 *
 * Runs at most once per `{project, region}` per browser tab session.
 */
const useBackgroundOperationsRefresh = () => {
  const queryClient = useQueryClient();
  const { getOttoToken } = useAuth();
  const { project, projectKey, region, hasScope } = useScopeKey();
  const { data: cached } = useCachedOperations();
  // Reactive retry driver. The production caller is persistently mounted and
  // never remounts, so a transient failure must re-run the effect via state —
  // bumping `refreshTick` (bounded by `retryCountRef`) is what re-triggers it.
  const [refreshTick, setRefreshTick] = useState(0);
  const retryCountRef = useRef(0);
  const lastScopeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasScope || !cached) return;
    if (Date.now() - cached.cachedAt < BACKGROUND_REFRESH_MIN_CACHE_AGE_MS) {
      return; // Cache is recent enough — skip the refresh to spare API load.
    }
    const scopeKey = `${projectKey}::${region}`;
    // Reset the retry budget whenever the scope changes so a new region/project
    // gets a fresh set of attempts.
    if (lastScopeRef.current !== scopeKey) {
      lastScopeRef.current = scopeKey;
      retryCountRef.current = 0;
    }
    if (backgroundRefreshedScopes.has(scopeKey)) return;
    // Mark eagerly so concurrent picker mounts in this tab dedupe to a single
    // refresh. `releaseClaim` UN-marks the scope whenever this run did not
    // complete a full pagination (token not ready, network blip mid-pagination,
    // unmount, or a StrictMode setup→cleanup→setup cycle), so a stuck partial
    // cache heals within the session instead of surviving the full 24h TTL.
    backgroundRefreshedScopes.add(scopeKey);

    let cancelled = false;
    // Only keep the scope marked when a full end-to-end pagination lands.
    let attemptCompleted = false;
    let ownsClaim = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    // Release only THIS run's claim, and only while it hasn't completed. Called
    // synchronously from cleanup so a StrictMode replacement setup finds the
    // scope free and re-runs, and from the async `finally` for the normal
    // incomplete path. Ownership-guarded so a cancelled run can't delete a
    // claim that a replacement run has since taken.
    const releaseClaim = () => {
      if (ownsClaim && !attemptCompleted) {
        backgroundRefreshedScopes.delete(scopeKey);
        ownsClaim = false;
      }
    };
    const run = async (): Promise<void> => {
      try {
        const token = await getOttoToken();
        if (!token && !isLocalMode() && !isArmMockMode()) return;

        // Page through /apiOperations end-to-end. Any error rejects the loop
        // immediately so a partial network response can't sneak in. In
        // parallel, refetch `/managedApis` so we can repair the malformed
        // `api.id` on each operation before persisting — without this the
        // recovered cache would still surface canonical Outlook / Office 365
        // ops under a `displayName:` group key and downstream consumers
        // (drill-in fetch, slug extraction) would silently break.
        const fresh: DiscoveryOperation[] = [];
        const connectorListPromise = fetchAllAzureConnectors({
          projectName: project,
          accessToken: token ?? '',
        }).catch((err) => {
          // Non-fatal — fall back to no repair (Map stays empty, ops persisted as-is).
          console.warn(
            `[useBackgroundOperationsRefresh] /managedApis fetch failed for '${region}' ` +
              `(repair skipped):`,
            err,
          );
          return [] as Connector[];
        });
        let pageParam: string | undefined;
        do {
          const page = await fetchAzureOperationsByPage(
            { projectName: project, accessToken: token ?? '' },
            pageParam,
          );
          if (cancelled) return;
          fresh.push(...page.data);
          pageParam = page.nextLink;
        } while (pageParam);

        if (cancelled) return;

        // Refresh built-ins for the active app on this aged-cache pass — before
        // the Azure size gate below can early-return — so the current app
        // recovers a short/stale built-in set immediately even when Azure is
        // unchanged. This region-scoped pass only covers the active app; other
        // apps in the region rely on their own per-{project,app} 2h
        // refetchInterval. `invalidateQueries` refetches active observers even
        // with refetch-on-mount disabled.
        queryClient.invalidateQueries({
          predicate: (q) => {
            const k = q.queryKey;
            return (
              Array.isArray(k) &&
              k[0] === 'operations' &&
              k[1] === 'builtin' &&
              k[2] === projectKey
            );
          },
        });

        const connectorList = await connectorListPromise;
        if (cancelled) return;

        // Mark the attempt complete only AFTER the last await + cancellation
        // check that could still abort before anything is applied. Marking
        // right after pagination meant a scope change / unmount during the
        // connector await returned without writing yet left the scope marked,
        // permanently skipping self-heal for it. Set here so the equal-count
        // path below (a valid "nothing to overwrite" result) still counts as
        // complete and is not retried.
        attemptCompleted = true;

        const repairIndex = buildConnectorIdentityIndex(connectorList);
        const freshRepaired = fresh.map((op) =>
          repairAzureOpIdentity(op, repairIndex),
        );

        const cachedAzureCount = cached.operations.filter(isAzureOperation).length;
        // Only overwrite when the fresh corpus is strictly larger. The HWM
        // gate inside writeOperationsCache will *also* enforce the 80%
        // floor as a second line of defense.
        if (freshRepaired.length <= cachedAzureCount) {
          return;
        }

        // Merge fresh Azure ops with the non-Azure portion of the cached
        // corpus (built-ins, custom). The non-Azure half is unaffected by
        // this refresh; preserving it avoids dropping built-ins until the
        // built-in source independently refetches.
        const nonAzure = cached.operations.filter((op) => !isAzureOperation(op));
        const merged = [...nonAzure, ...freshRepaired].map(trimOperationForCache);

        const accepted = await writeOperationsCache(region, merged);
        if (cancelled || !accepted) return;

        // Invalidate the cache read query and the Azure lazy query so the
        // picker re-renders against the recovered Azure corpus. Built-ins were
        // already invalidated above (and also refresh on their 2h interval).
        queryClient.invalidateQueries({ queryKey: ['operationsCache', region] });
        queryClient.invalidateQueries({
          queryKey: ['operationsLazy', 'azure', projectKey, region],
        });
      } catch (err) {
        console.warn(
          `[useBackgroundOperationsRefresh] Refresh failed for '${region}':`,
          err,
        );
      } finally {
        if (attemptCompleted) {
          retryCountRef.current = 0;
        } else {
          // Incomplete attempt (token not ready, mid-pagination error, or an
          // unmount / scope change). Release our own claim (if still held) so
          // the scope can be retried on a later trigger; only schedule the
          // in-place backed-off retry while still mounted (i.e. not cancelled).
          releaseClaim();
          if (!cancelled && retryCountRef.current < BACKGROUND_REFRESH_MAX_RETRIES) {
            const delay =
              BACKGROUND_REFRESH_RETRY_BASE_MS * 2 ** retryCountRef.current;
            retryCountRef.current += 1;
            retryTimer = setTimeout(() => {
              if (!cancelled) setRefreshTick((t) => t + 1);
            }, delay);
          }
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      // Release synchronously so a StrictMode replacement setup (or a scope
      // change) finds the claim free and re-runs instead of deduping to a
      // cancelled attempt that never applies a corpus.
      releaseClaim();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasScope, cached, project, projectKey, region, refreshTick]);

  // Re-arm the self-heal when the browser regains connectivity. The bounded
  // 2s/4s/8s retry burst above only covers a brief blip; a longer network (or
  // token) outage would exhaust it and, since the hook stays mounted with no
  // dependency change, never retry for the rest of the scope session. Resetting
  // the budget and bumping `refreshTick` on `online` re-runs the effect so
  // recovery stays possible without a reload.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const rearm = () => {
      retryCountRef.current = 0;
      setRefreshTick((t) => t + 1);
    };
    window.addEventListener('online', rearm);
    return () => window.removeEventListener('online', rearm);
  }, []);
};

/**
 * Root-component preloader. Drives all three lazy queries forward, fetching
 * the next Azure/Custom page as soon as the previous one settles, and
 * persists the merged corpus to IndexedDB once every source reports done.
 *
 * Returns `{ isLoading }` — true while any source still has pages outstanding
 * or its first fetch is in flight. Callers should NOT block UI on this — the
 * picker is expected to render immediately and degrade search quality
 * gracefully as new pages arrive.
 */
export const usePrefetchOperations = () => {
  const queryClient = useQueryClient();
  const { projectKey, region, hasScope } = useScopeKey();

  // Self-healing path: if the cache is older than the freshness window,
  // re-fetch in the background and overwrite if we recover more ops than
  // the cache currently holds. Recovers users stuck with a partial entry.
  useBackgroundOperationsRefresh();

  // Apply a region-gates change (from devtools / writeRegionGates) without a
  // reload: invalidate the built-in query so it refetches and re-reads the
  // gates fresh from IndexedDB. Mounted once (AppLayout), so a single listener.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Seed the defaults on first load so the `region-gates` row is always
    // visible/editable in the Application → IndexedDB panel.
    void seedRegionGatesIfMissing();
    const onGatesChanged = () => {
      void queryClient.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          q.queryKey[0] === 'operations' &&
          q.queryKey[1] === 'builtin',
      });
    };
    window.addEventListener(REGION_GATES_CHANGED_EVENT, onGatesChanged);
    return () => window.removeEventListener(REGION_GATES_CHANGED_EVENT, onGatesChanged);
  }, [queryClient]);

  // Connector identity index — used by the persistence diagnostic below
  // so canonical-strict counts agree with the merged-corpus log (both
  // resolve ops with stripped `api.id` + missing `api.name` via the
  // canonical `api.displayName`).
  const connectorIdentityIndex = useAzureConnectorIdentityIndex();

  const {
    isLoading: azureIsLoading,
    fetchNextPage: fetchNextAzurePage,
    hasNextPage: hasNextAzurePage,
    isFetchingNextPage: isFetchingNextAzurePage,
    status: azureStatus,
  } = useAzureOperationsLazyQuery();

  useEffect(() => {
    if (azureIsLoading || isFetchingNextAzurePage) return;
    if (!hasNextAzurePage) return;
    void fetchNextAzurePage();
  }, [azureIsLoading, fetchNextAzurePage, hasNextAzurePage, isFetchingNextAzurePage]);

  const {
    isLoading: customIsLoading,
    fetchNextPage: fetchNextCustomPage,
    hasNextPage: hasNextCustomPage,
    isFetchingNextPage: isFetchingNextCustomPage,
    status: customStatus,
  } = useCustomOperationsLazyQuery();

  useEffect(() => {
    if (customIsLoading || isFetchingNextCustomPage) return;
    if (!hasNextCustomPage) return;
    void fetchNextCustomPage();
  }, [customIsLoading, fetchNextCustomPage, hasNextCustomPage, isFetchingNextCustomPage]);

  const { isLoading: builtinIsLoading, status: builtinStatus } = useBuiltInOperationsQuery();

  const isLoading = useMemo(
    () =>
      azureIsLoading ||
      customIsLoading ||
      builtinIsLoading ||
      hasNextAzurePage ||
      hasNextCustomPage,
    [
      azureIsLoading,
      customIsLoading,
      builtinIsLoading,
      hasNextAzurePage,
      hasNextCustomPage,
    ],
  );

  // Persist the merged corpus once every source has finished. Track the most
  // recent {scope, count, malformed} we've written so reactive re-renders
  // don't replay the IDB write repeatedly. The malformed-id count is part of
  // the fingerprint so a repair-induced quality improvement (same op count,
  // fewer malformed `api.id`s) re-triggers a write — without this, a cache
  // poisoned during the first fetch (where `/apiOperations` finished before
  // `/managedApis` resolved the identity index) would stay poisoned for the
  // full 24h TTL even after the in-memory repair fixed the corpus.
  const { data: cached } = useCachedOperations();
  const lastWritten = useRef<{ key: string; count: number; malformed: number } | null>(null);
  const { data: merged } = useAllOperations();

  useEffect(() => {
    if (!hasScope || isLoading) return;
    if (!merged || merged.length === 0) return;

    // Cache hit — only write back when the in-memory corpus is strictly
    // higher quality than the persisted one (i.e. the `/managedApis`
    // identity index resolved and the repair fixed malformed `api.id`s the
    // cached snapshot was missing). Avoids resetting `cachedAt` on every
    // mount while still letting a poisoned cache heal itself within one
    // session instead of waiting for the full TTL to expire.
    const inMemoryMalformed = countMalformedAzureIds(merged);
    if (cached) {
      const cachedMalformed = countMalformedAzureIds(cached.operations);
      if (inMemoryMalformed >= cachedMalformed) return;
    }

    // Refuse to persist a partial corpus. If any source ended in an error
    // state (e.g. a transient 429 / 503 / network blip during page N of M,
    // or a built-in /operations fetch that errored), the in-memory corpus
    // contains only the pages/sources that succeeded — writing that subset
    // to IndexedDB would lock users into an incomplete operation list for
    // the entire 24h TTL. Wait for the next clean run; TanStack Query
    // auto-retries failed queries and a subsequent settle will satisfy
    // this gate.
    if (
      azureStatus !== 'success' ||
      customStatus !== 'success' ||
      builtinStatus !== 'success'
    ) {
      return;
    }

    // Guard against poisoning the cache with a built-ins-only corpus. If the
    // Azure source hasn't returned anything yet (or its `type` discriminator
    // doesn't match `isAzureOperation`), persisting now would short-circuit
    // every future load via the cached-fast-path in `useAzureOperationsLazyQuery`.
    // Bail out and wait for a subsequent settle that includes Azure ops.
    const azureCount = merged.filter(isAzureOperation).length;
    if (azureCount === 0) {
      return;
    }

    const scopeKey = `${projectKey}::${region}`;
    if (
      lastWritten.current &&
      lastWritten.current.key === scopeKey &&
      lastWritten.current.count === merged.length &&
      lastWritten.current.malformed === inMemoryMalformed
    ) {
      return;
    }
    lastWritten.current = {
      key: scopeKey,
      count: merged.length,
      malformed: inMemoryMalformed,
    };
    // Trim before persisting — the in-memory TanStack cache keeps the full
    // payload for any consumer that needs richer metadata in the live
    // session; IDB stores only the slim shape the picker + search index read.
    const trimmed = merged.map(trimOperationForCache);

    void writeOperationsCache(region, trimmed).then((accepted) => {
      if (accepted) {
        // Refresh the cache query so subsequent mounts see the fresh entry
        // and can short-circuit the network path.
        queryClient.invalidateQueries({ queryKey: ['operationsCache', region] });
      }
      // If `accepted === false`, the cache module already logged a warning
      // with the HWM details — nothing else to do here.
    });
  }, [hasScope, isLoading, merged, projectKey, region, cached, queryClient, azureStatus, customStatus, builtinStatus, connectorIdentityIndex]);

  return { isLoading };
};

/**
 * Heuristic for splitting the cached corpus back into "Azure" vs "BuiltIn"
 * buckets when hydrating from IndexedDB. Mirrors the type discriminator the
 * Otto control plane returns for managed-api operations.
 */
const isAzureOperation = (op: DiscoveryOperation): boolean =>
  (op.type ?? '').toLowerCase() === 'automationprojects/managedapis/apioperations';
